import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { services } from '../data/mock';

const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Collect all unique gallery images from services
  const allImages = services.reduce((acc, service) => {
    service.gallery.forEach((img) => {
      if (!acc.find((item) => item.image === img)) {
        acc.push({
          image: img,
          title: service.title,
          id: `${service.id}-${img}`,
        });
      }
    });
    return acc;
  }, []);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  };

  const nextImage = () => {
    const newIndex = (currentIndex + 1) % allImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(allImages[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentIndex - 1 + allImages.length) % allImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(allImages[newIndex]);
  };

  return (
    <section id="gallery" className="section-padding bg-[#F5F9E8]">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#2D5A4A] font-semibold text-sm uppercase tracking-wider">
            Our Work
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B3A3A] mt-3 mb-6">
            Project Gallery
          </h2>
          <p className="text-lg text-[#6B7B7B]">
            Browse through our portfolio of completed landscaping projects and see
            the quality of our work firsthand.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="gallery-grid">
          {allImages.slice(0, 12).map((item, index) => (
            <div
              key={item.id}
              className="gallery-item group"
              onClick={() => openLightbox(item, index)}
            >
              <img
                src={item.image}
                alt={item.title}
                loading="lazy"
              />
              <div className="gallery-item-overlay flex items-end justify-between p-4">
                <span className="text-white font-semibold text-sm">
                  {item.title}
                </span>
                <ZoomIn className="text-white" size={20} />
              </div>
            </div>
          ))}
        </div>

        {/* View More CTA */}
        <div className="text-center mt-12">
          <a href="#contact" className="btn-primary">
            Start Your Project Today
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center animate-fade-in"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
            aria-label="Close lightbox"
          >
            <X size={28} />
          </button>

          {/* Navigation */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Next image"
          >
            <ChevronRight size={28} />
          </button>

          {/* Image */}
          <div
            className="max-w-5xl max-h-[85vh] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.image}
              alt={selectedImage.title}
              className="gallery-image shadow-2xl"
            />
            <p className="text-white text-center mt-4 font-medium">
              {selectedImage.title}
            </p>
            <p className="text-white/60 text-center text-sm mt-1">
              {currentIndex + 1} / {allImages.length}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
